import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import SuccessModal from '../../components/modals/SuccessModal';
import { validateEmail as runEmailValidation } from '../../utils/emailValidation';
import { getForgotPasswordErrorDisplay } from './LoginPage';
import '../../styles/components/login.css';

// Hard-block format + disposable on Forgot Password. No typo suggestion —
// keeping the form silent about which domains we recognize avoids leaking
// hints during a recovery flow.
const validateEmail = (email: string): string => {
  const result = runEmailValidation(email, { blockDisposable: true, checkTypo: false });
  return result.ok ? '' : (result.error || 'Please enter a valid email address');
};

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const { forgotPassword } = useAuth();

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const [showSuccess, setShowSuccess] = useState(false);
  const [successStatus, setSuccessStatus] = useState<'success' | 'failed'>('success');
  const [successTitle, setSuccessTitle] = useState('');
  const [successSubtitle, setSuccessSubtitle] = useState('');

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setEmail(value);
    setEmailError(validateEmail(value));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const error = validateEmail(email);
    if (error) {
      setEmailError(error);
      return;
    }

    setIsLoading(true);
    try {
      const result = await forgotPassword(email);
      if (result.success) {
        setSuccessStatus('success');
        setSuccessTitle('Password Reset Link Sent');
        setSuccessSubtitle('Your password reset link has been sent. Open it from your inbox to set a new password — don’t forget to check spam if it doesn’t appear.');
      } else {
        const display = getForgotPasswordErrorDisplay(result.error);
        setSuccessStatus('failed');
        setSuccessTitle(display.title);
        setSuccessSubtitle(display.description);
      }
    } catch {
      const display = getForgotPasswordErrorDisplay();
      setSuccessStatus('failed');
      setSuccessTitle(display.title);
      setSuccessSubtitle(display.description);
    } finally {
      setIsLoading(false);
      setShowSuccess(true);
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
    if (successStatus === 'success') {
      navigate('/login');
    }
  };

  return (
    <div className="login-container" style={{ justifyContent: 'center' }}>
      <a
        href="https://smarteco.ai/"
        target="_blank"
        rel="noopener noreferrer"
        className="absolute top-6 left-8 h-[70px] cursor-pointer hover:opacity-80 transition-opacity"
      >
        <img
          src={`${process.env.PUBLIC_URL}/SVGIcons/NewSmartEcoLogo.svg`}
          alt="Smart Eco Logo"
          className="h-full w-full object-contain"
        />
      </a>

      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 sm:p-8 md:p-10 mx-4">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-6 sm:mb-8 whitespace-nowrap">
          Forgot password
        </h1>

        <form noValidate onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
          <div>
            <input
              type="email"
              id="forgot-email"
              value={email}
              onChange={handleEmailChange}
              className="w-full border border-[#0F796B] rounded-lg px-3 sm:px-4 py-3 bg-transparent text-[var(--color-text)] placeholder-[#0000004D] dark:placeholder-[#FFFFFF4D] outline-none focus:ring-1 focus:ring-[#0F796B] transition-all text-sm sm:text-base"
              placeholder="Enter your email"
              autoFocus
              required
            />
            {emailError && (
              <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {emailError}
              </p>
            )}
            <p className="text-xs sm:text-sm text-[var(--color-sub-title-text)] mt-3 leading-snug">
              We&apos;ll send a password reset link to this email if it matches an existing SmartEco account.
            </p>
          </div>

          <button
            type="submit"
            disabled={isLoading || !!emailError || !email}
            className="w-full rounded-full bg-[#0F796B] hover:bg-[#0c685c] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-3 text-sm sm:text-base transition-colors"
          >
            {isLoading ? (
              <span className="flex items-center justify-center">
                <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                Sending...
              </span>
            ) : (
              'Next'
            )}
          </button>

          <button
            type="button"
            onClick={handleBack}
            className="w-full text-center text-[var(--color-sub-title-text)] hover:text-[var(--color-text)] font-medium py-2"
          >
            Back
          </button>
        </form>
      </div>

      <SuccessModal
        isOpen={showSuccess}
        onClose={handleSuccessClose}
        title={successTitle}
        subtitle={successSubtitle}
        status={successStatus}
      />
    </div>
  );
};

export default ForgotPasswordPage;
